"use client";

import { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  LogIn, User, ScanLine, CheckCircle, XCircle, RotateCcw,
  Loader2, ArrowLeft, Plus, Minus, X, Search, Clock, ChevronRight,
  AlertTriangle, Package
} from 'lucide-react';
import BarcodeScanner from '@/components/BarcodeScanner';
import { STAFF_ROLES } from '@/lib/roles';
import { createCheckout, completeCheckin, getActiveCheckouts, type ActiveCheckoutStudent } from '@/actions/checkout';
import { lookupKioskStudent, verifyKioskStaffVidForReturn, verifyPinGate } from '@/actions/auth';
import { KIOSK_IDLE_MS, resolveKioskIdleAction } from '@/lib/kioskIdlePolicy';
import {
  searchKioskItemsByName, getKiosk25ItemDetails,
  resolveKioskScanLoan, getCheckoutUnreturnedCount,
  expandKiosk25CheckoutLines, type KioskSearchResult,
} from '@/actions/inventory';
import { useUsbScanner } from '@/lib/useUsbScanner';
import { todayLocalISO } from '@/lib/dates';

// ── Constants ─────────────────────────────────────────────────────────────────
const IDLE_MS = KIOSK_IDLE_MS;

const NAME_SEARCH_DEBOUNCE_MS = 350;

const DURATION_PRESETS = [
  { label: '1 hr',      minutes: 60 },
  { label: '2 hr',      minutes: 120 },
  { label: '4 hr',      minutes: 240 },
  { label: 'End of Day', minutes: -1 }, // special: calculated at submit
];

function endOfDayMinutes() {
  const now = new Date();
  const eod = new Date(now);
  eod.setHours(16, 45, 0, 0); // 4:45 PM — enough time to return before shift ends
  if (eod <= now) eod.setDate(eod.getDate() + 1);
  return Math.round((eod.getTime() - now.getTime()) / 60_000);
}

function dueDateFromMinutes(minutes: number): string {
  const mins = minutes === -1 ? endOfDayMinutes() : minutes;
  return new Date(Date.now() + mins * 60_000).toISOString();
}

function formatDueTime(minutes: number): string {
  const mins = minutes === -1 ? endOfDayMinutes() : minutes;
  return new Date(Date.now() + mins * 60_000).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

// ── Types ─────────────────────────────────────────────────────────────────────
type Step = 'home' | 'scan' | 'confirm' | 'done';

interface StaffUser  { id: string; vid: string; firstName: string; lastName: string; role: string; }
interface StudentInfo { id: string; vid: string; firstName: string; lastName: string; }

interface CartEntry {
  barcode: string | null;
  itemId: string | null;       // Inventory 2.5 identity — mutually exclusive with barcode
  itemName: string;
  unitLabel?: string;          // e.g. "A", "1" — shown so student knows which unit to grab
  qtyAvailable: number;        // caps the + button
  defaultDurationMinutes: number;
  qty: number;
  action: 'checkout' | 'return';
  checkoutId?: string;
  needsApproval?: boolean;
  /** Folder contents / attached items that travel with this scan (display only until confirm expands). */
  includes?: string[];
}

// A cart entry's true identity is item_id when present (2.5), barcode otherwise
// (V1/V2) — mirrors the same preference order used throughout checkout.ts/
// completeCheckin. Used for cart de-dup and for keying the active-checkout lookup.
function entryKey(e: { barcode: string | null; itemId: string | null }): string {
  return e.itemId ?? e.barcode ?? '';
}

type ActiveStudent = ActiveCheckoutStudent;

// ── Sub-components ────────────────────────────────────────────────────────────
function KioskCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-[var(--bg-surface)] rounded-2xl p-6 shadow-xl border border-[var(--border)] ${className}`}>
      {children}
    </div>
  );
}

function KioskHeader({ label, sub, onBack }: { label: string; sub?: string; onBack?: () => void }) {
  if (!onBack) {
    return (
      <div className="flex flex-col items-center text-center mb-6">
        <img
          src="/brand/puma-filmstrip-transparent.png"
          alt=""
          draggable={false}
          className="w-20 h-20 object-contain mb-3 select-none"
        />
        <div className="text-[10px] font-semibold uppercase tracking-widest text-brand">Bunker Box Kiosk</div>
        <div className="font-bold text-xl mt-0.5" style={{ color: 'var(--kiosk-title)' }}>{label}</div>
        {sub && <div className="text-xs mt-0.5" style={{ color: 'var(--kiosk-muted)' }}>{sub}</div>}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 mb-6">
      <button
        onClick={onBack}
        className="p-2 rounded-full hover:bg-[var(--bg-raised)] transition-colors flex-shrink-0"
        style={{ color: 'var(--kiosk-muted)' }}
      >
        <ArrowLeft size={18} />
      </button>
      <img
        src="/brand/puma-filmstrip-transparent.png"
        alt=""
        draggable={false}
        className="w-10 h-10 object-contain flex-shrink-0 select-none"
      />
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-brand">Bunker Box Kiosk</div>
        <div className="font-bold text-xl mt-0.5" style={{ color: 'var(--kiosk-title)' }}>{label}</div>
        {sub && <div className="text-xs mt-0.5" style={{ color: 'var(--kiosk-muted)' }}>{sub}</div>}
      </div>
    </div>
  );
}

function ActionBadge({ action }: { action: 'checkout' | 'return' }) {
  return action === 'checkout'
    ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 whitespace-nowrap">Checking Out</span>
    : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 whitespace-nowrap">Returning</span>;
}

// ── Main page ─────────────────────────────────────────────────────────────────
function KioskPageInner() {
  // Optional VID pre-fill from the staff dashboard's "Open Kiosk" shortcut — the PIN
  // itself is always typed fresh; see PinGate's initialVid comment for why this is
  // safe to do without weakening the walk-up terminal's own PIN-only trust model.
  const searchParams = useSearchParams();
  const initialVid = searchParams.get('vid') ?? undefined;

  // Step & actors — home is public; checkout/return both need staff VID+PIN (scoped tokens).
  const [step, setStep] = useState<Step>('home');
  const [staff, setStaff] = useState<StaffUser | null>(null);
  const [unlockToken, setUnlockToken] = useState<string | null>(null);
  const [unlockScope, setUnlockScope] = useState<'checkout' | 'return' | null>(null);
  const [student, setStudent] = useState<StudentInfo | null>(null);

  // Home screen
  const [activeTab, setActiveTab] = useState<'checkout' | 'return'>('checkout');
  const [activeStudents, setActiveStudents] = useState<ActiveStudent[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [staffVidInput, setStaffVidInput] = useState('');
  const [staffReturnPin, setStaffReturnPin] = useState('');
  const [staffVidError, setStaffVidError] = useState('');
  const [staffVidLoading, setStaffVidLoading] = useState(false);
  const staffVidRef = useRef<HTMLInputElement>(null);
  const staffReturnPinRef = useRef<HTMLInputElement>(null);

  // Check Out tab — inline staff VID + PIN (stays in tab; no full-page PinGate)
  const [checkoutStaffVid, setCheckoutStaffVid] = useState(initialVid ?? '');
  const [checkoutPin, setCheckoutPin] = useState('');
  const [checkoutUnlockError, setCheckoutUnlockError] = useState('');
  const [checkoutUnlockLoading, setCheckoutUnlockLoading] = useState(false);
  const checkoutPinRef = useRef<HTMLInputElement>(null);

  // Scan / cart
  const [cart, setCart] = useState<CartEntry[]>([]);
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeError, setBarcodeError] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);

  // Name search
  const [nameSearchResults, setNameSearchResults] = useState<KioskSearchResult[]>([]);
  const [nameSearchPending, setNameSearchPending] = useState(false);
  const nameSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nameSearchGen = useRef(0);

  // Student VID input
  const [vidInput, setVidInput] = useState('');
  const [studentError, setStudentError] = useState('');

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [resultMsg, setResultMsg] = useState('');
  const [resultOk, setResultOk] = useState(true);

  // Refs
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Active students loader ─────────────────────────────────────────────────
  const loadActiveStudents = useCallback(async () => {
    if (!unlockToken || !staff) {
      setActiveStudents([]);
      return;
    }
    setLoadingStudents(true);
    try {
      const students = await getActiveCheckouts({
        kioskUnlockToken: unlockToken,
        kioskStaffId: staff.id,
      });
      setActiveStudents(students);
    } catch (e) {
      console.error('loadActiveStudents:', e);
    } finally {
      setLoadingStudents(false);
    }
  }, [unlockToken, staff]);

  useEffect(() => {
    if (step === 'home' && activeTab === 'return' && unlockToken && staff) {
      loadActiveStudents();
      const interval = setInterval(loadActiveStudents, 30_000);
      return () => clearInterval(interval);
    }
  }, [step, activeTab, unlockToken, staff, loadActiveStudents]);

  // ── Idle timer ────────────────────────────────────────────────────────────
  const lockKiosk = useCallback(() => {
    setStaff(null);
    setUnlockToken(null);
    setUnlockScope(null);
    setStudent(null);
    setCart([]);
    setVidInput('');
    setStaffVidInput('');
    setStaffReturnPin('');
    setStaffVidError('');
    setCheckoutStaffVid(initialVid ?? '');
    setCheckoutPin('');
    setCheckoutUnlockError('');
    setBarcodeInput('');
    setBarcodeError('');
    setStudentError('');
    setNameSearchResults([]);
    setDurationMinutes(60);
    setActiveStudents([]);
    setStep('home');
  }, []);

  const resetToHome = useCallback(() => {
    setStudent(null);
    setCart([]);
    setVidInput('');
    setBarcodeInput('');
    setBarcodeError('');
    setStudentError('');
    setNameSearchResults([]);
    setDurationMinutes(60);
    setStep('home');
  }, []);

  const resetIdle = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    const unlocked = Boolean(staff && unlockToken);
    const action = resolveKioskIdleAction({ unlocked, step });
    if (action === 'none') return;
    idleTimer.current = setTimeout(() => {
      if (action === 'lock') lockKiosk();
      else resetToHome();
    }, IDLE_MS);
  }, [step, staff, unlockToken, lockKiosk, resetToHome]);

  useEffect(() => {
    resetIdle();
    return () => { if (idleTimer.current) clearTimeout(idleTimer.current); };
  }, [resetIdle]);

  useEffect(() => {
    return () => { if (nameSearchTimer.current) clearTimeout(nameSearchTimer.current); };
  }, []);

  // ── Auto-focus on scan step ───────────────────────────────────────────────
  useEffect(() => {
    if (step === 'scan') {
      setBarcodeInput('');
      setBarcodeError('');
      setTimeout(() => barcodeInputRef.current?.focus(), 50);
    }
  }, [step]);

  // ── USB scanner listener (active on scan step, overlay closed) ────────────
  useUsbScanner(
    (val) => { setBarcodeInput(val); addToCart({ barcode: val }); },
    { enabled: step === 'scan' && !scannerOpen }
  );

  function groupKioskSearchHits(results: KioskSearchResult[]): KioskSearchResult[] {
    const grouped = new Map<string, KioskSearchResult>();
    for (const r of results) {
      if ((r.availableCount ?? 0) <= 0) continue;
      const existing = grouped.get(r.groupKey);
      if (!existing) grouped.set(r.groupKey, { ...r, availableCount: 1 });
      else existing.availableCount++;
    }
    return [...grouped.values()].slice(0, 8);
  }

  async function runKioskSearch(query: string): Promise<KioskSearchResult[]> {
    const results = await searchKioskItemsByName(query);
    return groupKioskSearchHits(results);
  }

  function handleBarcodeInputChange(val: string) {
    setBarcodeInput(val);
    setBarcodeError('');
    if (nameSearchTimer.current) clearTimeout(nameSearchTimer.current);
    const q = val.trim();
    if (!q) {
      nameSearchGen.current += 1;
      setNameSearchResults([]);
      setNameSearchPending(false);
      return;
    }
    const gen = ++nameSearchGen.current;
    setNameSearchPending(true);
    nameSearchTimer.current = setTimeout(async () => {
      try {
        const grouped = await runKioskSearch(q);
        if (gen !== nameSearchGen.current) return;
        setNameSearchResults(grouped);
      } catch {
        if (gen !== nameSearchGen.current) return;
        setNameSearchResults([]);
      } finally {
        if (gen === nameSearchGen.current) setNameSearchPending(false);
      }
    }, NAME_SEARCH_DEBOUNCE_MS);
  }

  async function submitScanOrSearch() {
    const val = barcodeInput.trim();
    if (!val || lookingUp) return;
    if (nameSearchTimer.current) clearTimeout(nameSearchTimer.current);
    const gen = ++nameSearchGen.current;
    setNameSearchPending(true);
    let hits: KioskSearchResult[] = [];
    try {
      hits = await runKioskSearch(val);
    } catch {
      hits = [];
    }
    if (gen !== nameSearchGen.current) return;
    setNameSearchPending(false);
    setNameSearchResults(hits);

    const exact = hits.find((r) => r.barcode && r.barcode.toLowerCase() === val.toLowerCase());
    if (exact) {
      addToCart(exact.itemId ? { itemId: exact.itemId } : { barcode: exact.barcode }, exact.itemName);
      return;
    }
    if (hits.length === 1) {
      const r = hits[0];
      addToCart(r.itemId ? { itemId: r.itemId } : { barcode: r.barcode }, r.itemName);
      return;
    }
    if (hits.length > 1) return;
    addToCart({ barcode: val });
  }

  // ── Add to cart ───────────────────────────────────────────────────────────
  // Inventory 2.5 only — resolve by itemId (name search) or barcode (scan).
  // Returns are checked before kiosk-eligibility so an out item can always come back
  // even if it was removed from the allowlist.
  async function addToCart(identity: { barcode?: string | null; itemId?: string | null }, prefillName?: string) {
    if (!student) return;
    resetIdle();
    const b = identity.barcode?.trim() || null;
    const knownItemId = identity.itemId ?? null;
    if (!b && !knownItemId) return;

    const key = knownItemId ?? b!;
    // Duplicate → +1 qty
    const existing = cart.find(e => entryKey(e) === key);
    if (existing) {
      setCart(prev => prev.map(e => entryKey(e) === key ? { ...e, qty: e.qty + 1 } : e));
      setBarcodeInput('');
      setNameSearchResults([]);
      setTimeout(() => barcodeInputRef.current?.focus(), 30);
      return;
    }

    setLookingUp(true);
    setBarcodeError('');

    const item25 = await getKiosk25ItemDetails(
      knownItemId ? { itemId: knownItemId } : { barcode: b! }
    );

    // Loan check can succeed even when the catalog row is missing (rare) — try by
    // whatever identity we have so returns aren't blocked on V1/legacy lookups.
    const loan = await resolveKioskScanLoan(
      item25
        ? { itemId: item25.itemId, barcode: b }
        : knownItemId
          ? { itemId: knownItemId }
          : { barcode: b },
      student.id
    );

    if (loan.kind === 'staff_desk') {
      setBarcodeError(
        `"${loan.itemName}" is on a staff checkout — return it at the equipment desk, not the kiosk.`
      );
      setLookingUp(false);
      setTimeout(() => barcodeInputRef.current?.focus(), 30);
      return;
    }

    if (loan.kind === 'kiosk_return') {
      setCart(prev => {
        const next = [...prev];
        for (const line of loan.lines) {
          const lineKey = line.itemId ?? line.barcode ?? '';
          if (next.some(e => entryKey(e) === lineKey && e.action === 'return')) continue;
          next.push({
            barcode: line.barcode,
            itemId: line.itemId,
            itemName: line.itemName,
            qtyAvailable: 999,
            defaultDurationMinutes: 60,
            qty: 1,
            action: 'return',
            checkoutId: line.checkoutId,
          });
        }
        return next;
      });
      setBarcodeInput('');
      setNameSearchResults([]);
      setLookingUp(false);
      setTimeout(() => barcodeInputRef.current?.focus(), 30);
      return;
    }

    if (!item25) {
      setBarcodeError('Barcode not found in inventory.');
      setLookingUp(false);
      setTimeout(() => barcodeInputRef.current?.focus(), 30);
      return;
    }

    const needsApproval = !item25.eligible && item25.requestPolicy === 'standalone';
    if (!item25.eligible && !needsApproval) {
      setBarcodeError('This item is not available for student checkout.');
      setLookingUp(false);
      setTimeout(() => barcodeInputRef.current?.focus(), 30);
      return;
    }

    if (!needsApproval && item25.qtyAvailable <= 0) {
      setBarcodeError('This item is currently unavailable — all units are checked out.');
      setLookingUp(false);
      setTimeout(() => barcodeInputRef.current?.focus(), 30);
      return;
    }

    const entry: CartEntry = {
      barcode: b,
      itemId: item25.itemId,
      itemName: prefillName ?? item25.displayName,
      qtyAvailable: item25.qtyAvailable,
      defaultDurationMinutes: item25.defaultDurationMinutes,
      qty: 1,
      action: 'checkout',
      needsApproval,
      includes: item25.includes.length > 0 ? item25.includes : undefined,
    };

    setCart(prev => {
      const next = [...prev, entry];
      const firstCheckout = next.find(e => e.action === 'checkout');
      if (firstCheckout && prev.filter(e => e.action === 'checkout').length === 0) {
        setDurationMinutes(firstCheckout.defaultDurationMinutes);
      }
      return next;
    });
    setBarcodeInput('');
    setNameSearchResults([]);
    setLookingUp(false);
    setTimeout(() => barcodeInputRef.current?.focus(), 30);
  }

  // ── Student lookup ────────────────────────────────────────────────────────
  async function lookupStudent(overrideVid?: string) {
    const vid = (overrideVid ?? vidInput).trim();
    if (!vid) return;
    setStudentError('');
    const result = await lookupKioskStudent(vid);
    if ('error' in result) { setStudentError(result.error); return; }
    setStudent({ id: result.id, vid: result.vid, firstName: result.firstName, lastName: result.lastName });
    setStep('scan');
  }

  /** Return tab — staff VID + PIN (return-scoped token; cannot create checkouts). */
  async function unlockStaffForReturn() {
    const vid = staffVidInput.trim();
    const pin = staffReturnPin.trim();
    if (!vid || !pin) {
      setStaffVidError('Enter your staff ID and PIN.');
      return;
    }
    setStaffVidLoading(true);
    setStaffVidError('');
    try {
      const result = await verifyKioskStaffVidForReturn(vid, pin, [...STAFF_ROLES]);
      if ('error' in result) {
        setStaffVidError(result.error);
        setStaffReturnPin('');
        setTimeout(() => staffReturnPinRef.current?.focus(), 30);
        return;
      }
      setStaff(result.user);
      setUnlockToken(result.unlockToken);
      setUnlockScope('return');
      setStaffVidInput('');
      setStaffReturnPin('');
      setActiveTab('return');
    } finally {
      setStaffVidLoading(false);
    }
  }

  /** Check Out tab — VID + PIN inline (same verifyPinGate as PinGate, no page change). */
  async function unlockStaffForCheckout() {
    const vid = checkoutStaffVid.trim();
    const pin = checkoutPin.trim();
    if (!vid || !pin) {
      setCheckoutUnlockError('Enter your staff ID and PIN.');
      return;
    }
    setCheckoutUnlockLoading(true);
    setCheckoutUnlockError('');
    try {
      const result = await verifyPinGate(vid, pin, [...STAFF_ROLES]);
      if ('error' in result) {
        setCheckoutUnlockError(result.error);
        setCheckoutPin('');
        setTimeout(() => checkoutPinRef.current?.focus(), 30);
        return;
      }
      setStaff(result.user);
      setUnlockToken(result.unlockToken);
      setUnlockScope('checkout');
      setCheckoutPin('');
      setCheckoutUnlockError('');
      setActiveTab('checkout');
    } finally {
      setCheckoutUnlockLoading(false);
    }
  }

  // USB scanner on Return staff-VID gate — fills VID, then staff enters PIN
  useUsbScanner(
    (val) => {
      setStaffVidInput(val);
      setStaffVidError('');
      setTimeout(() => staffReturnPinRef.current?.focus(), 30);
    },
    { enabled: step === 'home' && activeTab === 'return' && !staff }
  );

  // ── Return from active student list ──────────────────────────────────────
  function selectActiveStudent(as: ActiveStudent) {
    setStudent({ id: as.studentId, vid: '', firstName: as.name.split(' ')[0], lastName: as.name.split(' ').slice(1).join(' ') });
    const entries: CartEntry[] = as.items.map(i => ({
      barcode: i.barcode,
      itemId: i.itemId,
      itemName: i.itemName,
      qtyAvailable: 999, // returns are not subject to availability cap
      defaultDurationMinutes: 60,
      qty: 1,
      action: 'return' as const,
      checkoutId: i.checkoutId,
    }));
    setCart(entries);
    setStep('confirm');
    resetIdle();
  }

  // ── Submit ────────────────────────────────────────────────────────────────
  async function handleConfirm() {
    if (!staff || !student) return;
    if (!unlockToken) {
      setResultMsg(
        unlockScope === 'return'
          ? 'Return session expired — scan your staff ID again.'
          : 'Kiosk unlock expired — staff must unlock again with ID and PIN.'
      );
      setResultOk(false);
      setStep('done');
      setTimeout(() => { lockKiosk(); setResultMsg(''); }, 4000);
      return;
    }
    setSubmitting(true);
    const msgs: string[] = [];
    try {
      const checkoutItems = cart.filter(e => e.action === 'checkout');
      const returnItems   = cart.filter(e => e.action === 'return');

      if (checkoutItems.length > 0) {
        const dueDate = dueDateFromMinutes(durationMinutes);
        // Expand each 2.5 scan into concrete leaves (folder contents / set members /
        // attached children). Legacy barcode-only lines pass through unchanged.
        const perRootLines: {
          barcode: string | null;
          itemId: string | null;
          itemName: string;
          qty: number;
          groupId?: string | null;
          groupLabel?: string | null;
        }[] = [];
        for (const e of checkoutItems) {
          if (!e.itemId) {
            perRootLines.push({
              barcode: e.barcode,
              itemId: null,
              itemName: e.itemName,
              qty: e.qty,
            });
            continue;
          }
          const lines = await expandKiosk25CheckoutLines([e.itemId]);
          if (lines.length === 0) {
            perRootLines.push({
              barcode: e.barcode,
              itemId: e.itemId,
              itemName: e.itemName,
              qty: e.qty,
            });
            continue;
          }
          for (const l of lines) {
            perRootLines.push({
              barcode: l.barcode,
              itemId: l.itemId,
              itemName: l.itemName,
              qty: l.qty * e.qty,
              groupId: l.groupId,
              groupLabel: l.groupLabel,
            });
          }
        }
        const merged = new Map<string, (typeof perRootLines)[number]>();
        for (const line of perRootLines) {
          const key = line.itemId ?? `b:${line.barcode}`;
          const prev = merged.get(key);
          if (prev) prev.qty += line.qty;
          else merged.set(key, { ...line });
        }

        const checkoutResult = await createCheckout({
          studentId: student.id,
          staffId: staff.id,
          project: 'Kiosk Walk-In',
          projectType: 'Other',
          isClassAssignment: false,
          notes: '',
          dueDate,
          source: 'kiosk',
          kioskUnlockToken: unlockToken,
          items: [...merged.values()],
        });
        if (checkoutResult.error || !checkoutResult.id) {
          throw new Error(checkoutResult.error || 'Checkout failed.');
        }
        const n = [...merged.values()].reduce((s, e) => s + e.qty, 0);
        msgs.push(`Checked out ${n} item${n !== 1 ? 's' : ''}. Due ${dueDate}.`);
      }

      if (returnItems.length > 0) {
        // Group by checkoutId
        const groups: Record<string, CartEntry[]> = {};
        for (const e of returnItems) {
          const key = e.checkoutId ?? 'unknown';
          (groups[key] ??= []).push(e);
        }
        // Fetch total unreturned item count per checkout to set partial correctly
        for (const [checkoutId, items] of Object.entries(groups)) {
          if (checkoutId === 'unknown') continue;
          const count = await getCheckoutUnreturnedCount(checkoutId);
          const returningCount = items.reduce((s, e) => s + e.qty, 0);
          await completeCheckin({
            checkoutId,
            items: items.map(e => ({ barcode: e.barcode, itemId: e.itemId, qty: e.qty, conditionIn: 'Good' })),
            partial: returningCount < count,
            requireSource: 'kiosk',
            kioskStaffId: staff.id,
            kioskUnlockToken: unlockToken,
          });
        }
        const n = returnItems.reduce((s, e) => s + e.qty, 0);
        msgs.push(`Returned ${n} item${n !== 1 ? 's' : ''}. Thank you!`);
      }

      setResultMsg(msgs.join(' '));
      setResultOk(true);
    } catch (e: unknown) {
      const msg = (e as { message?: string }).message ?? 'Something went wrong.';
      setResultMsg(msg);
      setResultOk(false);
      // Unlock token expired/invalid — force PinGate again so a stale browser
      // tab can't keep writing after the signed window ends.
      if (/kiosk unlock|return session|staff vid/i.test(msg)) {
        setSubmitting(false);
        setStep('done');
        setTimeout(() => { lockKiosk(); setResultMsg(''); }, 4000);
        return;
      }
    }
    setSubmitting(false);
    setStep('done');
    setTimeout(resetToHome, 4000);
  }

  // ── Computed ──────────────────────────────────────────────────────────────
  const checkoutCount = cart.filter(e => e.action === 'checkout').reduce((s, e) => s + e.qty, 0);
  const returnCount   = cart.filter(e => e.action === 'return').reduce((s, e) => s + e.qty, 0);
  const hasCheckoutItems = cart.some(e => e.action === 'checkout');

  // ── Done step ─────────────────────────────────────────────────────────────
  if (step === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center space-y-4">
          {resultOk
            ? <CheckCircle size={64} className="mx-auto text-green-400" />
            : <XCircle size={64} className="mx-auto text-red-400" />
          }
          <p className="text-[var(--text-1)] font-bold text-xl">{resultOk ? 'Done!' : 'Error'}</p>
          <p className="text-[var(--text-2)] text-sm leading-relaxed">{resultMsg}</p>
          <p className="text-[var(--text-3)] text-xs">Returning to home…</p>
        </div>
      </div>
    );
  }

  // ── Confirm step ──────────────────────────────────────────────────────────
  if (step === 'confirm') {
    const dueDate = dueDateFromMinutes(durationMinutes);
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8" onPointerMove={resetIdle}>
        <div className="w-full max-w-sm">
          <KioskHeader label="Confirm" onBack={() => setStep('scan')} />
          <KioskCard>
            {/* Student */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-[var(--bg-raised)] mb-4">
              <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center text-brand-fg font-bold text-sm flex-shrink-0">
                {student?.firstName[0]}{student?.lastName[0]}
              </div>
              <div>
                <div className="text-sm font-semibold text-[var(--text-1)]">{student?.firstName} {student?.lastName}</div>
                {student?.vid && <div className="text-[10px] text-[var(--text-3)] font-mono">{student.vid}</div>}
              </div>
            </div>

            {/* Items */}
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {cart.map((e, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--bg-raised)]">
                  {e.needsApproval
                    ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400">APPROVED</span>
                    : <ActionBadge action={e.action} />
                  }
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--text-1)] truncate">{e.itemName}</div>
                    {e.unitLabel ? (
                      <div className="text-[10px] text-brand font-bold">Unit {e.unitLabel}</div>
                    ) : e.barcode ? (
                      <div className="text-[10px] text-[var(--text-3)] font-mono">{e.barcode}</div>
                    ) : null}
                    {e.includes && e.includes.length > 0 && e.action === 'checkout' && (
                      <div className="text-[10px] text-[var(--text-3)] mt-0.5 truncate">
                        Includes {e.includes.length}: {e.includes.slice(0, 3).join(', ')}
                        {e.includes.length > 3 ? ` +${e.includes.length - 3}` : ''}
                      </div>
                    )}
                  </div>
                  {e.qty > 1 && <span className="text-xs text-[var(--text-3)]">×{e.qty}</span>}
                </div>
              ))}
            </div>

            {/* Duration chip */}
            {hasCheckoutItems && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-brand/10 mb-4">
                <Clock size={13} className="text-brand flex-shrink-0" />
                <span className="text-xs text-brand font-semibold">
                  {durationMinutes === -1 ? 'Due end of day' : `Due ${dueDate}`}
                </span>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setStep('scan')}
                className="flex-1 py-3 rounded-xl text-sm font-semibold border border-[var(--border)] text-[var(--text-2)] hover:bg-[var(--bg-raised)] transition-colors"
              >
                ← Add more
              </button>
              <button
                onClick={handleConfirm}
                disabled={submitting}
                className="flex-1 py-3 rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--amber)', color: 'var(--primary-foreground)' }}
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
                Confirm
              </button>
            </div>
          </KioskCard>
        </div>
      </div>
    );
  }

  // ── Scan step ─────────────────────────────────────────────────────────────
  if (step === 'scan') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8" onPointerMove={resetIdle}>
        <div className="w-full max-w-sm">
          <KioskHeader
            label="Scan items"
            sub={`${student?.firstName} ${student?.lastName}`}
            onBack={resetToHome}
          />
          <KioskCard className="space-y-4">
            {/* Scan input */}
            <div className="relative">
              <div className="flex gap-2">
                <input
                  ref={barcodeInputRef}
                  value={barcodeInput}
                  onChange={e => handleBarcodeInputChange(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void submitScanOrSearch();
                    }
                  }}
                  placeholder="Scan barcode, name, or category…"
                  className="flex-1 border border-[var(--border)] rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand bg-[var(--bg-raised)] text-[var(--text-1)]"
                />
                <button
                  onClick={() => setScannerOpen(true)}
                  className="px-3 rounded-xl border border-[var(--border)] text-[var(--text-2)] hover:bg-[var(--bg-raised)] transition-colors"
                  title="Camera scanner"
                >
                  {lookingUp ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />}
                </button>
              </div>

              {(nameSearchResults.length > 0 || (barcodeInput.trim().length >= 2 && !nameSearchPending && nameSearchResults.length === 0)) && (
                <div className="absolute top-full left-0 right-0 z-10 mt-1 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl overflow-hidden">
                  {nameSearchResults.length === 0 ? (
                    <div className="px-3 py-2.5 text-xs text-[var(--text-3)]">No kiosk items match that search.</div>
                  ) : nameSearchResults.map(r => (
                    <button
                      key={r.groupKey}
                      onClick={() => {
                        setNameSearchResults([]);
                        addToCart(r.itemId ? { itemId: r.itemId } : { barcode: r.barcode }, r.itemName);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--text-1)] hover:bg-[var(--bg-raised)] transition-colors text-left"
                    >
                      <Search size={12} className="text-[var(--text-3)] flex-shrink-0" />
                      <span className="flex-1 truncate">{r.itemName}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${r.availableCount > 1 ? 'bg-green-500/20 text-green-400' : 'bg-[var(--bg-raised)] text-[var(--text-2)]'}`}>
                        {r.availableCount} avail.
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Error */}
            {barcodeError && (
              <div className="flex items-start gap-2 text-xs text-red-500 bg-red-500/10 px-3 py-2 rounded-lg">
                <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                {barcodeError}
              </div>
            )}

            {/* Cart list */}
            {cart.length > 0 && (
              <div className="space-y-1.5 max-h-52 overflow-y-auto">
                {cart.map((e, i) => (
                  <div
                    key={i}
                    className={[
                      'flex items-center gap-2 px-3 py-2.5 rounded-xl',
                      e.needsApproval ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-[var(--bg-raised)]',
                    ].join(' ')}
                  >
                    {e.needsApproval ? (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 flex-shrink-0">
                        APPROVAL
                      </span>
                    ) : (
                      <ActionBadge action={e.action} />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[var(--text-1)] truncate">{e.itemName}</div>
                      {e.unitLabel ? (
                        <div className="text-[10px] text-brand font-bold mt-0.5">Unit {e.unitLabel}</div>
                      ) : e.barcode ? (
                        <div className="text-[10px] text-[var(--text-3)] font-mono mt-0.5">{e.barcode}</div>
                      ) : null}
                      {e.includes && e.includes.length > 0 && e.action === 'checkout' && (
                        <div className="text-[10px] text-[var(--text-3)] mt-0.5 truncate">
                          +{e.includes.length} included
                        </div>
                      )}
                    </div>
                    {e.needsApproval ? (
                      <button
                        onClick={() => setCart(prev => prev.map((c, ci) => ci === i ? { ...c, needsApproval: false } : c))}
                        className="text-[10px] font-bold px-2 py-1 rounded-lg bg-amber-500 text-[#121212] hover:bg-amber-400 transition-colors flex-shrink-0"
                      >
                        Staff OK
                      </button>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setCart(prev => prev.map((c, ci) => ci === i ? { ...c, qty: Math.max(1, c.qty - 1) } : c))}
                          className="w-5 h-5 flex items-center justify-center rounded text-[var(--text-3)] hover:bg-[var(--border)] transition-colors"
                        >
                          <Minus size={10} />
                        </button>
                        <span className="text-xs font-semibold text-[var(--text-1)] w-4 text-center">{e.qty}</span>
                        <button
                          onClick={() => setCart(prev => prev.map((c, ci) =>
                            ci === i ? { ...c, qty: Math.min(c.qty + 1, c.action === 'return' ? 999 : c.qtyAvailable) } : c
                          ))}
                          disabled={e.action === 'checkout' && e.qty >= e.qtyAvailable}
                          className="w-5 h-5 flex items-center justify-center rounded text-[var(--text-3)] hover:bg-[var(--border)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Plus size={10} />
                        </button>
                      </div>
                    )}
                    <button
                      onClick={() => setCart(prev => prev.filter((_, ci) => ci !== i))}
                      className="w-5 h-5 flex items-center justify-center rounded text-[var(--text-3)] hover:text-red-400 transition-colors ml-1"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Approval notice */}
            {cart.some(e => e.needsApproval) && (
              <div className="flex items-start gap-2 text-xs text-amber-500 bg-amber-500/10 px-3 py-2 rounded-lg">
                <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                This is not a pre-approved item. Tapping Staff OK grants it to {student?.firstName} {student?.lastName}.
              </div>
            )}

            {/* Duration picker */}
            {hasCheckoutItems && (
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-[var(--text-3)] mb-2">Duration</div>
                <div className="grid grid-cols-4 gap-1.5">
                  {DURATION_PRESETS.map(p => (
                    <button
                      key={p.label}
                      onClick={() => setDurationMinutes(p.minutes)}
                      className={[
                        'py-2 rounded-lg text-xs font-semibold transition-colors',
                        durationMinutes === p.minutes
                          ? 'bg-brand text-brand-fg'
                          : 'bg-[var(--bg-raised)] text-[var(--text-2)] hover:bg-[var(--border)]',
                      ].join(' ')}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5 mt-2 px-1">
                  <Clock size={11} className="text-brand flex-shrink-0" />
                  <span className="text-xs text-[var(--text-3)]">
                    Due back by <span className="text-brand font-semibold">{formatDueTime(durationMinutes)}</span>
                  </span>
                </div>
              </div>
            )}

            {/* Continue button */}
            {cart.length > 0 && (
              <button
                onClick={() => setStep('confirm')}
                disabled={cart.some(e => e.needsApproval)}
                className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: 'var(--amber)', color: 'var(--primary-foreground)' }}
              >
                Continue
                {checkoutCount > 0 && <span className="opacity-70">· {checkoutCount} out</span>}
                {returnCount > 0 && <span className="opacity-70">· {returnCount} in</span>}
                <ChevronRight size={14} />
              </button>
            )}
          </KioskCard>
        </div>

        {scannerOpen && (
          <BarcodeScanner
            onScan={v => { setScannerOpen(false); addToCart({ barcode: v }); }}
            onClose={() => setScannerOpen(false)}
          />
        )}
      </div>
    );
  }

  // ── Home step ─────────────────────────────────────────────────────────────
  const today = todayLocalISO();
  const canCheckout = unlockScope === 'checkout';
  const canReturn = unlockScope === 'checkout' || unlockScope === 'return';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <KioskHeader label="Equipment Kiosk" />

        {/* Staff chip */}
        {staff ? (
          <div className="flex items-center gap-2 mb-4 px-1">
            <div className="w-5 h-5 rounded-full bg-brand flex items-center justify-center text-brand-fg font-bold text-[9px] flex-shrink-0">
              {staff.firstName[0]}
            </div>
            <span className="text-xs text-[var(--text-3)]">
              {staff.firstName} {staff.lastName}
              {unlockScope === 'return' ? ' · returns' : ' · unlocked'}
            </span>
            <button onClick={lockKiosk} className="ml-auto text-[10px] text-[var(--text-3)] hover:text-[var(--text-2)]">Lock</button>
          </div>
        ) : (
          <div className="mb-4 px-1 text-xs text-[var(--text-3)]">Choose Check Out or Return to begin.</div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-4 bg-[var(--bg-raised)] p-1 rounded-xl">
          {(['checkout', 'return'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={[
                'flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors capitalize',
                activeTab === tab ? 'bg-brand text-brand-fg' : 'text-[var(--text-2)] hover:text-[var(--text-1)]',
              ].join(' ')}
            >
              {tab === 'checkout' ? 'Check Out' : `Return${canReturn && activeStudents.length > 0 ? ` (${activeStudents.length})` : ''}`}
            </button>
          ))}
        </div>

        {/* Checkout tab — VID+PIN inline when locked; student VID when unlocked */}
        {activeTab === 'checkout' && (
          <KioskCard>
            {!canCheckout ? (
              <form
                className="space-y-4"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                data-bwignore="true"
                data-form-type="other"
                onSubmit={e => { e.preventDefault(); void unlockStaffForCheckout(); }}
              >
                <p className="text-[11px] text-[var(--text-3)]">
                  Staff ID + PIN to check gear out
                  {unlockScope === 'return' ? ' (returns-only session — enter PIN to check out)' : ''}.
                </p>
                <div>
                  <label className="block text-xs font-semibold text-[var(--text-2)] mb-1.5 uppercase tracking-wide">
                    Staff ID
                  </label>
                  <input
                    autoFocus={!checkoutStaffVid}
                    name="vfe-kiosk-staff-vid"
                    value={checkoutStaffVid}
                    onChange={e => { setCheckoutStaffVid(e.target.value); setCheckoutUnlockError(''); }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        checkoutPinRef.current?.focus();
                      }
                    }}
                    placeholder="Scan staff ID…"
                    className="w-full border border-[var(--border)] rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand bg-[var(--bg-raised)] text-[var(--text-1)]"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bwignore="true"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[var(--text-2)] mb-1.5 uppercase tracking-wide">
                    PIN
                  </label>
                  <input
                    ref={checkoutPinRef}
                    autoFocus={!!checkoutStaffVid}
                    type="text"
                    inputMode="numeric"
                    name="vfe-kiosk-staff-pin"
                    value={checkoutPin}
                    onChange={e => {
                      setCheckoutPin(e.target.value.replace(/\D/g, '').slice(0, 8));
                      setCheckoutUnlockError('');
                    }}
                    placeholder="••••"
                    className="w-full border border-[var(--border)] rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand bg-[var(--bg-raised)] text-[var(--text-1)] tracking-widest"
                    style={{ WebkitTextSecurity: 'disc' } as React.CSSProperties}
                    autoComplete="one-time-code"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                  />
                </div>
                {checkoutUnlockError && (
                  <div className="text-xs text-red-500 bg-red-500/10 px-3 py-2 rounded-lg">{checkoutUnlockError}</div>
                )}
                <button
                  type="submit"
                  disabled={checkoutUnlockLoading}
                  className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: 'var(--amber)', color: 'var(--primary-foreground)' }}
                >
                  {checkoutUnlockLoading ? <Loader2 size={15} className="animate-spin" /> : <LogIn size={15} />}
                  Continue
                </button>
              </form>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[var(--text-2)] mb-1.5 uppercase tracking-wide">
                    Student ID
                  </label>
                  <input
                    autoFocus
                    value={vidInput}
                    onChange={e => { setVidInput(e.target.value); setStudentError(''); }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const val = (e.currentTarget as HTMLInputElement).value.trim();
                        if (val) lookupStudent(val);
                      }
                    }}
                    placeholder="NNxxxx"
                    className="w-full border border-[var(--border)] rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand bg-[var(--bg-raised)] text-[var(--text-1)]"
                  />
                </div>
                {studentError && (
                  <div className="text-xs text-red-500 bg-red-500/10 px-3 py-2 rounded-lg">{studentError}</div>
                )}
                <button
                  onClick={() => lookupStudent()}
                  className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
                  style={{ backgroundColor: 'var(--amber)', color: 'var(--primary-foreground)' }}
                >
                  <LogIn size={15} /> Continue
                </button>
              </div>
            )}
          </KioskCard>
        )}

        {/* Return tab — staff VID+PIN, then active kiosk loans */}
        {activeTab === 'return' && (
          <div className="space-y-2">
            {!canReturn ? (
              <KioskCard>
                <form
                  className="space-y-4"
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  data-form-type="other"
                  onSubmit={e => { e.preventDefault(); void unlockStaffForReturn(); }}
                >
                  <p className="text-[11px] text-[var(--text-3)]">
                    Staff ID + PIN to process returns (cannot check gear out).
                  </p>
                  <div>
                    <label className="block text-xs font-semibold text-[var(--text-2)] mb-1.5 uppercase tracking-wide">
                      Staff ID
                    </label>
                    <input
                      ref={staffVidRef}
                      autoFocus={!staffVidInput}
                      name="vfe-kiosk-return-staff-vid"
                      value={staffVidInput}
                      onChange={e => { setStaffVidInput(e.target.value); setStaffVidError(''); }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          staffReturnPinRef.current?.focus();
                        }
                      }}
                      placeholder="Scan staff ID…"
                      className="w-full border border-[var(--border)] rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand bg-[var(--bg-raised)] text-[var(--text-1)]"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-bwignore="true"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[var(--text-2)] mb-1.5 uppercase tracking-wide">
                      PIN
                    </label>
                    <input
                      ref={staffReturnPinRef}
                      autoFocus={!!staffVidInput}
                      type="text"
                      inputMode="numeric"
                      name="vfe-kiosk-return-staff-pin"
                      value={staffReturnPin}
                      onChange={e => {
                        setStaffReturnPin(e.target.value.replace(/\D/g, '').slice(0, 8));
                        setStaffVidError('');
                      }}
                      placeholder="••••"
                      className="w-full border border-[var(--border)] rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand bg-[var(--bg-raised)] text-[var(--text-1)] tracking-widest"
                      style={{ WebkitTextSecurity: 'disc' } as React.CSSProperties}
                      autoComplete="one-time-code"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      data-form-type="other"
                    />
                  </div>
                  {staffVidError && (
                    <div className="text-xs text-red-500 bg-red-500/10 px-3 py-2 rounded-lg">{staffVidError}</div>
                  )}
                  <button
                    type="submit"
                    disabled={staffVidLoading}
                    className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: 'var(--amber)', color: 'var(--primary-foreground)' }}
                  >
                    {staffVidLoading ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />}
                    Start Returns
                  </button>
                </form>
              </KioskCard>
            ) : (
              <>
                {loadingStudents && (
                  <div className="text-center py-8 text-[var(--text-3)] text-sm">
                    <Loader2 size={20} className="animate-spin mx-auto mb-2" />
                    Loading…
                  </div>
                )}
                {!loadingStudents && activeStudents.length === 0 && (
                  <div className="text-center py-10 text-[var(--text-3)]">
                    <Package size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No active kiosk checkouts</p>
                  </div>
                )}
                {activeStudents.map(as => {
                  const overdue = as.dueDate < today;
                  return (
                    <button
                      key={as.studentId}
                      onClick={() => selectActiveStudent(as)}
                      className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)] hover:border-brand/40 hover:bg-[var(--bg-raised)] transition-all text-left"
                    >
                      <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
                        style={{ backgroundColor: 'var(--amber)', color: 'var(--primary-foreground)' }}>
                        {as.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[var(--text-1)] truncate">{as.name}</div>
                        <div className={`text-xs mt-0.5 ${overdue ? 'text-amber-400' : 'text-[var(--text-3)]'}`}>
                          {as.itemCount} item{as.itemCount !== 1 ? 's' : ''} · due {as.dueDate}
                          {overdue && ' · OVERDUE'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {overdue && <AlertTriangle size={14} className="text-amber-400" />}
                        <RotateCcw size={14} className="text-[var(--text-3)]" />
                        <ChevronRight size={14} className="text-[var(--text-3)]" />
                      </div>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function KioskPage() {
  return (
    <Suspense fallback={
      <div className="flex h-screen items-center justify-center bg-[var(--bg-base)]">
        <div className="text-sm text-[var(--text-2)]">Loading…</div>
      </div>
    }>
      <KioskPageInner />
    </Suspense>
  );
}
